import { useEffect, useRef, useState } from 'react';
import { useViewAsClient } from '@/auth/viewAsClient';
import { useIsRealAdmin } from '@/auth/useIsAdmin';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { AssistantDrawer } from '@/components/assistant/AssistantDrawer';
import { useClientAccess } from '@/auth/useClientAccess';
import { NAV } from './nav';
import { SearchProvider, useSearch } from './search';
import { setApiTokenGetter } from '@/data/api';

function titleFor(pathname: string): string {
  if (pathname.startsWith('/assistant')) return 'Ask tritonIQ';
  if (pathname.startsWith('/referrals')) return 'Referral Rewards';
  // Longest matching nav path wins (so /content/request → contentIQ).
  const match = [...NAV]
    .sort((a, b) => b.path.length - a.path.length)
    .find((n) => (n.path === '/' ? pathname === '/' : pathname.startsWith(n.path)));
  return match?.label ?? 'tritonIQ';
}

function AppShellInner() {
  const { pathname } = useLocation();
  const { getToken, orgId } = useAuth();
  const { setQuery } = useSearch();
  const prevOrg = useRef(orgId);
  const [navOpen, setNavOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const { access } = useClientAccess();

  // Give the API helper a way to fetch the current Clerk session token.
  useEffect(() => {
    setApiTokenGetter(() => getToken());
  }, [getToken]);

  // Every query is scoped to the active client (org). Switching clients in the
  // switcher must re-pull everything against the new org — clearing the cache
  // in place proved unreliable (stale token/observers), so we do a hard reload
  // to the dashboard. Clerk persists the newly-selected org, so the reload
  // comes back authenticated as that client with fresh data.
  useEffect(() => {
    if (prevOrg.current && orgId && prevOrg.current !== orgId) {
      window.location.assign('/');
      return;
    }
    prevOrg.current = orgId;
  }, [orgId]);

  /* The top bar is sticky, so anything else that sticks to the top of the page
     (the draft editor's toolbar) has to stick below it, not under it. Its
     height changes with the viewport and the view-as banner, so it is
     measured rather than written into the CSS. */
  useEffect(() => {
    const top = document.querySelector<HTMLElement>('.dash-top');
    if (!top) return;
    const set = () => document.documentElement.style.setProperty('--dash-top-h', `${top.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(top);
    return () => ro.disconnect();
  }, []);

  // Reset the search box and close the mobile nav when navigating between pages.
  useEffect(() => {
    setQuery('');
    setNavOpen(false);
  }, [pathname, setQuery]);

  const realAdmin = useIsRealAdmin();
  const [viewingAsClient, setViewAsClient] = useViewAsClient();

  return (
    <div className="dash">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      <div className="dash-main">
        {realAdmin && viewingAsClient && (
          <div className="dash-viewas" role="status">
            <span>Viewing as a client — admin tools and the content generator are hidden, exactly as a Client Manager sees this account.</span>
            <button type="button" onClick={() => setViewAsClient(false)}>Exit client view</button>
          </div>
        )}
        <Topbar title={titleFor(pathname)} onMenu={() => setNavOpen(true)} onAssistant={access?.canAssistant && !pathname.startsWith('/assistant') ? () => setAskOpen(true) : undefined} />
        <div className="dash-body">
          <Outlet />
        </div>
      </div>
      {access?.canAssistant && <AssistantDrawer open={askOpen} onClose={() => setAskOpen(false)} />}
    </div>
  );
}

export function AppShell() {
  return (
    <SearchProvider>
      <AppShellInner />
    </SearchProvider>
  );
}
